// Minimal hand-rolled SVG sparkline (no chart lib, per design rules).
// Extracted from Fund.tsx (16.d.5) so both the investor's own /fund view and
// the manager's per-investor drilldown (FundInvestorDetail) render one chart.
// The component plots Y as-is; the caller picks which currency figure to pass.
// compactAxis (opt-in): treasury-magnitude consumers pass true so axis labels
// render "$1.04M" instead of a clipped "$1040000.00" — the 56-unit label
// gutter was sized for ~1.00 unit prices. Mirrors fmtUSDcompact's convention
// (format.ts:20). Default false keeps the fund charts byte-identical.
export function NavSparkline({ series, currency = 'USD', compactAxis = false }: { series: { x: string; y: number }[]; currency?: string; compactAxis?: boolean }) {
  const W = 1200, H = 160, padR = 56, padB = 22, padT = 12;
  const xs = series.map((_, i) => (i / Math.max(1, series.length - 1)) * (W - padR));
  const ys = series.map(p => p.y);
  const lo = Math.min(...ys), hi = Math.max(...ys), range = hi - lo || 1;
  const ny = ys.map(y => padT + (1 - (y - lo) / range) * (H - padT - padB));
  const path = xs.map((x, i) => `${i === 0 ? 'M' : 'L'} ${x.toFixed(1)} ${(ny[i] ?? 0).toFixed(1)}`).join(' ');
  const glyph = currency === 'EUR' ? '€' : currency === 'USD' ? '$' : '';
  // Axis decimals scale to the series range, not a fixed 2. A unitized fund
  // priced at 1.00 (n8) moves in the 4th–6th decimal as fees accrue, so a
  // hard toFixed(2) printed the SAME label at both ends ("$1.00 / $1.00") and
  // the axis said nothing. Sparkline axes may round (editorial summary-context
  // exception) — they may not collapse to a single value.
  const axisDp = range >= 1 ? 2 : range >= 0.01 ? 4 : 6;
  const axisLabel = (v: number) => {
    if (!compactAxis) return `${glyph}${v.toFixed(axisDp)}${glyph ? '' : ` ${currency}`}`;
    const abs = Math.abs(v);
    const sign = v < 0 ? '−' : '';
    const body = abs >= 1e9 ? `${(abs / 1e9).toFixed(2)}B`
      : abs >= 1e6 ? `${(abs / 1e6).toFixed(2)}M`
      : abs >= 1e3 ? `${(abs / 1e3).toFixed(1)}K`
      : abs.toFixed(0);
    return `${sign}${glyph}${body}${glyph ? '' : ` ${currency}`}`;
  };
  return (
    <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" style={{ width: '100%', height: H, display: 'block' }}>
      {[0, 0.5, 1].map(t => <line key={t} x1={0} x2={W - padR} y1={padT + t * (H - padT - padB)} y2={padT + t * (H - padT - padB)} stroke="var(--rule-soft)" strokeWidth="0.5" />)}
      {[1, 0].map(t => <text key={t} x={W - padR + 6} y={padT + t * (H - padT - padB) + 4} fontFamily="var(--mono)" fontSize="10" fill="var(--ink-3)">{axisLabel(lo + (1 - t) * range)}</text>)}
      <path d={path} fill="none" stroke="var(--accent)" strokeWidth="1.5" />
    </svg>
  );
}
